import { ChevronDown } from "lucide-react";
import {
  type ReactNode,
  Suspense,
  lazy,
  useCallback,
  useRef,
  useState,
} from "react";
import { Form } from "react-router";
import { ClientOnly } from "remix-utils/client-only";

import { type BuilderContent } from "@builder.io/sdk-react";
import { SCREENS } from "@tsf/tailwind-config/lib/constants";

import { DrawerRendered } from "~/components/account-drawer/account-drawer-retry";
import { Link } from "~/components/link";
import { OverlayBoundary } from "~/components/overlay-boundary";
import { type OverlayFailure, retryOverlay } from "~/components/overlay-retry";
import { SITE_LOGOS } from "~/config/site-logos";
import { useURL } from "~/contexts";
import SvgSearchIcon from "~/hec/assets/icons/search-icon";
import SvgUser from "~/hec/assets/icons/user";
import { ModelMap } from "~/hec/builder/model-map";
import { VisibleChromeContent } from "~/hec/builder/visible-chrome-content";
import { CartReminder } from "~/hec/components/organisms/cart-reminder/cart-reminder";
import {
  DEFAULT_BALANCE_POPUP_TEXT,
  HAS_TPA_BALANCE_BY_SITE,
} from "~/hec/components/tpa-balance/constants";
import { TpaBalance } from "~/hec/components/tpa-balance/tpa-balance";
import { useAuth } from "~/hec/context/auth-context";
import { useBuilder } from "~/hec/hooks/useBuilder";
import { useHoverPopover } from "~/hec/hooks/useHoverPopover";
import useMediaQuery from "~/hec/hooks/useMediaQuery";
import { useOverlaysReady } from "~/hec/hooks/useOverlaysReady";
import { PprHole } from "~/hec/ppr/ppr-hole";
import { type SiteId, bySiteId, siteCodeToId } from "~/hec/utils/site-id";
import type { Customer } from "~/lib/app.types";
import { Minicart } from "~/routes/resources/basket";

import { RouteChangeProgress } from "../nprogress/route-change-progress";
// develop replaced the bare CioAutocomplete with the boundary-wrapped variant;
// the hotfix added RouteChangeProgress. Both are wanted, and the unwrapped
// CioAutocomplete import goes — nothing references it after the merge.
import { SafeCioAutocomplete } from "../search/cio-autocomplete-boundary";
import { accountDropdownRowClasses } from "./account-menu-classes";
import { AlertBar } from "./alert-bar";
import type { NavigationCategory } from "./mobile-nav/types";

// COM-189 wave 4: the header's closed-at-paint overlays load lazily so their
// module graphs (vaul + drawer/scroll-area primitives, the mobile nav tree,
// the search-overlay dialog) leave the always-modulepreloaded layout chunk —
// they were the bytes the hero image queued behind (PR #1000's Load-Delay
// finding). All three render NOTHING while closed, so SSR output and the
// hydration render are unchanged; they mount on the overlays-ready signal
// (idle/first interaction) or immediately when opened, whichever comes first.
const HecMobileNav = lazy(() =>
  import("./hec-mobile-nav").then(m => ({ default: m.HecMobileNav })),
);
const MobileSearchOverlay = lazy(() =>
  import("../search/mobile-search-overlay").then(m => ({
    default: m.MobileSearchOverlay,
  })),
);
const AccountMenuDrawerOverlay = lazy(() => import("./account-menu-drawer"));
// COM-189 wave 4B: the desktop account flyout's Popover half — the header's
// last static path to the radix-popover / floating-ui graph.
const AccountFlyoutOverlay = lazy(() => import("./account-flyout"));

/**
 * HEC Header — matches the Builder.io "Full Header" content structure.
 *
 * Layout:
 *  1. Announcement bar (HeaderBanner)
 *  2. Sticky main nav bar (StickyWrapper — CSS Grid 30%/40%/30%)
 *  3. Share-of-Wallet category links bar (affiliate-nav-bar)
 *  4. TPABalance (skipped for now)
 */

// Per-site lookup maps — fed into bySiteId() instead of inlining
// `siteCode === "..."` ternaries so the multi-site lint stays happy.
const MOBILE_LOGO_HIDDEN_AT_BY_SITE: Record<SiteId, string> = {
  HSASTORE: "md:hidden",
  FSASTORE: "md:hidden",
  WDH: "md:hidden",
};
const SHOWS_TABLET_WORDMARK_BY_SITE: Record<SiteId, boolean> = {
  HSASTORE: true,
  FSASTORE: true,
  WDH: true,
};
const TABLET_LOGO_WIDTH_BY_SITE: Record<SiteId, number> = {
  HSASTORE: 150,
  FSASTORE: 150,
  WDH: 190,
};
const MOBILE_SEARCH_ROW_ALWAYS_VISIBLE_BY_SITE: Record<SiteId, boolean> = {
  HSASTORE: true,
  FSASTORE: false,
  WDH: false,
};
// Mirror of `MOBILE_SEARCH_ROW_ALWAYS_VISIBLE_BY_SITE`: only sites without an
// always-visible mobile search row need the toggle button.
const SHOWS_MOBILE_SEARCH_TOGGLE_BY_SITE: Record<SiteId, boolean> = {
  HSASTORE: false,
  FSASTORE: true,
  WDH: true,
};
const HAS_SOW_BAR_BY_SITE: Record<SiteId, boolean> = {
  HSASTORE: true,
  FSASTORE: true,
  WDH: false,
};
// SFCC-12333 scopes the returning-shopper cart reminder to FSA + HSA.
const SHOWS_CART_REMINDER_BY_SITE: Record<SiteId, boolean> = {
  HSASTORE: true,
  FSASTORE: true,
  WDH: false,
};
const DESKTOP_LOGO_SIZE_BY_SITE: Record<SiteId, { w: number; h: number }> = {
  HSASTORE: { w: 140, h: 33 },
  FSASTORE: { w: 146, h: 30 },
  WDH: { w: 180, h: 54 },
};

// ---------------------------------------------------------------------------
// SearchBar with CIO autocomplete
// ---------------------------------------------------------------------------

function HeaderSearchBarFallback() {
  const url = useURL();
  return (
    <Form method="get" action={url("/search")} className="relative">
      <input
        type="search"
        name="q"
        placeholder="Search Eligible Products"
        className="h-14 w-full rounded-lg border border-foreground bg-white py-2 pl-4 pr-10 font-grotesque text-sm text-foreground outline-none transition-colors focus:border-accent focus:ring-1 focus:ring-accent"
        aria-label="Search products"
        autoComplete="off"
      />
      <button
        type="submit"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-accent"
        aria-label="Submit search"
      >
        <SvgSearchIcon
          width={18}
          height={18}
          className="[stroke-width:0.5] lg:[stroke-width:1]"
        />
      </button>
    </Form>
  );
}

function HeaderSearchBar() {
  return (
    <ClientOnly fallback={<HeaderSearchBarFallback />}>
      {() => <SafeCioAutocomplete variant="header" />}
    </ClientOnly>
  );
}

// Three-bar mobile menu glyph — matches production Builder icon
// (`MobileMenu` icon block, 22×18 viewBox).
function MobileMenuIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="18"
      viewBox="0 0 22 18"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path fillRule="evenodd" d="M0 0h22v2H0zm0 8h22v2H0zm0 8h22v2H0z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Account menu — renders Builder "Account Dropdown" content. Desktop (>= sm /
// 600px, the closest breakpoint to 544) is a hover/click flyout; below that it
// opens as a left-side drawer, matching the hamburger nav (HecMobileNav).
// ---------------------------------------------------------------------------

// Shared trigger styling — used by the flyout, the drawer, and the
// no-Builder-content fallback link.
const accountTriggerClasses =
  "flex items-center gap-1 text-sm lg:text-base text-blue-04 font-grotesque transition-opacity hover:opacity-80 max-lg:pr-[10px] lg:ml-3 lg:mr-[50px]";

// `accountDropdownRowClasses` moved to ./account-menu-classes.ts — shared with
// the lazily-loaded mobile drawer half (COM-189 wave 4).

// Trigger content matches SFRA account/header.isml + mobileHeader.isml:
// guest -> "Log In" text (no icon); logged-in -> person icon + first name
// (name desktop-only; mobile shows the icon alone). An empty first name
// renders icon-only, mirroring SFRA's <isprint value="${firstName}"> emitting
// nothing — there is no text fallback.
function AccountTriggerContent({
  isLoggedIn,
  firstName,
  chevronSize,
  chevronStroke,
}: {
  isLoggedIn: boolean;
  firstName?: string;
  chevronSize: number;
  chevronStroke: number;
}) {
  return (
    <>
      {isLoggedIn ? (
        <>
          <SvgUser
            aria-hidden="true"
            width={18}
            height={20}
            className="text-minicart-accent"
          />
          {firstName ? (
            <span className="hidden font-medium lg:inline">{firstName}</span>
          ) : null}
        </>
      ) : (
        <span className="font-medium">Log In</span>
      )}
      <ChevronDown size={chevronSize} strokeWidth={chevronStroke} />
    </>
  );
}

// Desktop (>= sm / 600px): Radix Popover opened on hover or click. The
// popover half lives in ./account-flyout.tsx and loads lazily (COM-189 wave
// 4B) — this half owns the trigger button and the hover state, so a hover or
// click that lands before the chunk buffers as `open=true` and the flyout
// shows on arrival. Never SSRed (AccountPopover picks the drawer branch on the
// server), so the standalone attrs below only cover the brief client window
// before the overlays-ready signal.
function AccountFlyout({
  isLoggedIn,
  firstName,
  dropdownContent,
  apiKey,
}: {
  isLoggedIn: boolean;
  firstName?: string;
  dropdownContent: BuilderContent;
  apiKey: string;
}) {
  const {
    open,
    setOpen,
    handleOpen,
    handleClose,
    triggerRef,
    onCloseAutoFocus,
  } = useHoverPopover<HTMLButtonElement>();
  const overlaysReady = useOverlaysReady();
  const url = useURL();

  // PR #1141 (COM-189 wave 4 follow-up): a rejected flyout chunk used to park
  // the trigger for the session. Remember the failure and make the NEXT click
  // retry (guarded reload for a module-load failure, `/account` or `/login`
  // when the reload guard is spent, ONE in-place remount for a render crash)
  // — overlay-retry.ts. `remountAttempted` bounds the remount: a chunk that
  // evaluates but throws is cached by React.lazy like a failed import, so if
  // the overlay fails again after a remount the next click goes to the page
  // instead of beaconing another "successful" remount over a dead button. It
  // re-arms when the remounted flyout has rendered its OPEN content and
  // survived that commit — `DrawerRendered`, the layout's sentinel, hosted
  // inside `PopoverContent` via `renderedSentinel` (round 7) — and on a click
  // that reaches the working overlay (no failure pending). The render-time
  // re-arm is the one that matters here: this flyout opens on HOVER, so after
  // click → remount → working flyout the shopper may never click the trigger
  // again; re-armed on the click alone, the bound stayed spent for the page's
  // life and a LATER unrelated crash sent the first click to the page instead
  // of the in-place remount the first crash of a failure is promised.
  const [failure, setFailure] = useState<OverlayFailure | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const remountAttempted = useRef(false);
  const rearmRemount = useCallback(() => {
    remountAttempted.current = false;
  }, []);
  const handleTriggerClick = () => {
    if (failure) {
      retryOverlay("account-flyout", failure, {
        fallbackHref: url(isLoggedIn ? "/account" : "/login"),
        remountAttempted: remountAttempted.current,
        onRemount: () => {
          remountAttempted.current = true;
          setFailure(null);
          setRetryToken(token => token + 1);
          setOpen(true);
        },
      });
      return;
    }
    remountAttempted.current = false;
    setOpen(prev => !prev);
  };

  const renderTrigger = (standalone: boolean) => (
    <button
      ref={triggerRef}
      type="button"
      className={accountTriggerClasses}
      aria-label={isLoggedIn ? "My account" : "Sign in"}
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      onClick={handleTriggerClick}
      data-testid="account-popover-trigger"
      {...(standalone
        ? {
            "aria-haspopup": "dialog" as const,
            "aria-expanded": false,
            "data-state": "closed",
          }
        : {})}
    >
      <AccountTriggerContent
        isLoggedIn={isLoggedIn}
        firstName={firstName}
        chevronSize={20}
        chevronStroke={3}
      />
    </button>
  );

  if (!overlaysReady && !open) {
    return renderTrigger(true);
  }

  return (
    // OverlayBoundary (PR #1042 blocker 3): a rejected flyout chunk degrades
    // to the plain trigger for the session instead of unmounting the page.
    <OverlayBoundary
      overlay="account-flyout"
      fallback={renderTrigger(true)}
      onError={setFailure}
      retryToken={retryToken}
    >
      <Suspense fallback={renderTrigger(true)}>
        <AccountFlyoutOverlay
          open={open}
          onOpenChange={setOpen}
          onHoverOpen={handleOpen}
          onHoverClose={handleClose}
          onCloseAutoFocus={onCloseAutoFocus}
          trigger={renderTrigger(false)}
          dropdownContent={dropdownContent}
          apiKey={apiKey}
          renderedSentinel={<DrawerRendered onRendered={rearmRemount} />}
        />
      </Suspense>
    </OverlayBoundary>
  );
}

// Mobile (< sm / 600px): a right-anchored vaul drawer hosting the same Builder
// "Account Dropdown" content. The drawer subtree itself lives in
// ./account-menu-drawer.tsx and loads lazily (COM-189 wave 4) — this half owns
// the SSR-visible trigger button and the `open` state. The static
// aria-haspopup / aria-expanded / data-state attributes replicate what the
// Radix `DrawerTrigger` used to render, so the SSR document reads the same.
function AccountMenuDrawer({
  isLoggedIn,
  firstName,
  dropdownContent,
  apiKey,
}: {
  isLoggedIn: boolean;
  firstName?: string;
  dropdownContent: BuilderContent;
  apiKey: string;
}) {
  const [open, setOpen] = useState(false);
  const overlaysReady = useOverlaysReady();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const url = useURL();

  // PR #1141 (COM-189 wave 4 follow-up): same next-tap retry as the desktop
  // flyout, same one-remount bound, same render-time re-arm through
  // `renderedSentinel` (round 7) — a rejected drawer chunk must not leave
  // the account icon dead for the session (overlay-retry.ts).
  const [failure, setFailure] = useState<OverlayFailure | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const remountAttempted = useRef(false);
  const rearmRemount = useCallback(() => {
    remountAttempted.current = false;
  }, []);
  const handleTriggerClick = () => {
    if (failure) {
      retryOverlay("account-menu-drawer", failure, {
        fallbackHref: url(isLoggedIn ? "/account" : "/login"),
        remountAttempted: remountAttempted.current,
        onRemount: () => {
          remountAttempted.current = true;
          setFailure(null);
          setRetryToken(token => token + 1);
          setOpen(true);
        },
      });
      return;
    }
    remountAttempted.current = false;
    setOpen(prev => !prev);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={accountTriggerClasses}
        aria-label={isLoggedIn ? "My account" : "Sign in"}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-state={open ? "open" : "closed"}
        data-testid="account-popover-trigger"
        onClick={handleTriggerClick}
      >
        <AccountTriggerContent
          isLoggedIn={isLoggedIn}
          firstName={firstName}
          chevronSize={20}
          chevronStroke={3}
        />
      </button>
      {/* Mounted (chunk warmed) on the ready signal; a tap before that forces
          the mount and the drawer opens as soon as the chunk lands. Closed it
          renders nothing, so pre-mounting is free. */}
      {(overlaysReady || open) && (
        // OverlayBoundary (PR #1042 blocker 3): a rejected drawer chunk
        // degrades to trigger-only instead of unmounting the page.
        <OverlayBoundary
          overlay="account-menu-drawer"
          fallback={null}
          onError={setFailure}
          retryToken={retryToken}
        >
          <Suspense fallback={null}>
            <AccountMenuDrawerOverlay
              open={open}
              onOpenChange={setOpen}
              isLoggedIn={isLoggedIn}
              returnFocusRef={triggerRef}
              dropdownContent={dropdownContent}
              apiKey={apiKey}
              renderedSentinel={<DrawerRendered onRendered={rearmRemount} />}
            />
          </Suspense>
        </OverlayBoundary>
      )}
    </>
  );
}

function AccountPopover({
  isLoggedIn,
  accountDropdownContent,
  apiKey,
}: {
  isLoggedIn: boolean;
  accountDropdownContent?: BuilderContent | null;
  apiKey: string;
}) {
  const url = useURL();
  const { user } = useAuth();
  // The closest breakpoint to 544px is `sm` (600px). At >= 600px the menu is a
  // desktop flyout; below it, it opens as a drawer like the hamburger nav.
  // `useMediaQuery` is SSR-safe (false on the server and first client render),
  // so the trigger button hydrates identically and only the overlay primitive
  // swaps after mount — no hydration mismatch.
  const isFlyout = useMediaQuery(`(min-width: ${SCREENS.sm})`);

  const firstName = user?.firstName;

  // If no Builder content is available, fall back to a plain control. Logged in:
  // link to the SFRA-owned account area. Logged out: open the global auth drawer
  // via the `#login` hash (the drawer listens for it) rather than the retired
  // /login page.
  if (!accountDropdownContent) {
    const triggerInner = (
      <AccountTriggerContent
        isLoggedIn={isLoggedIn}
        firstName={firstName}
        chevronSize={14}
        chevronStroke={2}
      />
    );
    return isLoggedIn ? (
      <Link
        to={url("/account")}
        prefetch="intent"
        aria-label="My account"
        className={accountTriggerClasses}
      >
        {triggerInner}
      </Link>
    ) : (
      <a href="#login" aria-label="Sign in" className={accountTriggerClasses}>
        {triggerInner}
      </a>
    );
  }

  // COM-189 wave 4C: the dropdown's ChromeContent (full chrome registry) is
  // rendered INSIDE the lazy overlay halves — building the JSX here would keep
  // the overlay-only block definitions in the synchronous header graph.
  return isFlyout ? (
    <AccountFlyout
      isLoggedIn={isLoggedIn}
      firstName={firstName}
      dropdownContent={accountDropdownContent as BuilderContent}
      apiKey={apiKey}
    />
  ) : (
    <AccountMenuDrawer
      isLoggedIn={isLoggedIn}
      firstName={firstName}
      dropdownContent={accountDropdownContent as BuilderContent}
      apiKey={apiKey}
    />
  );
}

// ---------------------------------------------------------------------------
// Main header
// ---------------------------------------------------------------------------

export function HecHeader({
  isLoggedIn,
  categories = [],
  hostname = "",
  navDrawerContent,
  affiliateNavBar,
  alertBarContent,
  tpaBannerContent,
  siteCode = "hsa",
  accountDropdownContent,
}: {
  isLoggedIn: boolean;
  user: Customer | undefined;
  categories?: NavigationCategory[] | Promise<NavigationCategory[]>;
  hostname?: string;
  navDrawerContent?: BuilderContent | null;
  affiliateNavBar?: BuilderContent | null;
  alertBarContent?: any;
  tpaBannerContent?: string | null;
  siteCode?: string;
  accountDropdownContent?: BuilderContent | null;
}) {
  const url = useURL();
  const { config } = useBuilder();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  // Gates the lazy overlay mounts below (mobile nav drawer + search overlay);
  // false during SSR/hydration, so those subtrees stay exactly as absent from
  // the server document as they were when statically imported-but-closed.
  const overlaysReady = useOverlaysReady();
  // The trigger that opened the overlay — focus returns here on close.
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  // Hidden input used to keep the iOS soft keyboard up across the overlay open
  // (see the warm-up input near the end of the render + openMobileSearch).
  const searchWarmupRef = useRef<HTMLInputElement>(null);
  const logo = SITE_LOGOS[siteCode as "hsa" | "fsa" | "wdh"] ?? null;
  const siteId =
    siteCodeToId[siteCode as keyof typeof siteCodeToId] ?? "HSASTORE";
  const mobileLogoHiddenAt = bySiteId(siteId, MOBILE_LOGO_HIDDEN_AT_BY_SITE);
  const showsTabletWordmark = bySiteId(siteId, SHOWS_TABLET_WORDMARK_BY_SITE);
  const mobileSearchRowAlwaysVisible = bySiteId(
    siteId,
    MOBILE_SEARCH_ROW_ALWAYS_VISIBLE_BY_SITE,
  );
  const showsMobileSearchToggle = bySiteId(
    siteId,
    SHOWS_MOBILE_SEARCH_TOGGLE_BY_SITE,
  );
  const hasSowBar = bySiteId(siteId, HAS_SOW_BAR_BY_SITE);
  const showsCartReminder = bySiteId(siteId, SHOWS_CART_REMINDER_BY_SITE);
  const hasTpaBalance = bySiteId(siteId, HAS_TPA_BALANCE_BY_SITE);
  const desktopLogoSize = bySiteId(siteId, DESKTOP_LOGO_SIZE_BY_SITE);
  const tabletLogoWidth = bySiteId(siteId, TABLET_LOGO_WIDTH_BY_SITE);

  const openMobileSearch = () => {
    // Focus the hidden warm-up input synchronously inside this tap gesture so
    // iOS raises the soft keyboard; the overlay then transfers focus to the
    // real search input on mount, keeping the keyboard up. preventScroll keeps
    // the page from jumping while the warm-up input is off-screen.
    searchWarmupRef.current?.focus({ preventScroll: true });
    setMobileSearchOpen(true);
  };

  return (
    <>
      {/* -- 1. Alert Bar (announcement + tabs + TPA banner) -- */}
      <AlertBar
        alertBarContent={alertBarContent}
        tpaBannerContent={tpaBannerContent}
        siteCode={siteCode}
        hostname={hostname}
      />

      {/* -- 2. Sticky Main Nav (acts as the semantic <header>). Promoted out
              of any wrapping element so position:sticky resolves against the
              full-page containing block — stays at top throughout the scroll. */}
      <header className="sticky top-0 z-50 bg-white">
        {/* Main nav row */}
        <div className="relative grid grid-cols-[auto_1fr_auto] items-center px-3 py-1.5 sm:px-4 sm:py-2.5 lg:grid-cols-[30%_40%_30%] lg:px-6 lg:py-2">
          {/* Mobile/tablet logo — absolutely centered on the row so its
              position is independent of the side-column widths. Two variants
              (icon vs WDH tablet wordmark); only one ever renders at a time
              based on `mobileLogoHiddenAt` + `showsTabletWordmark`. */}
          {logo && (
            <Link
              to={url("/")}
              aria-label="Go to homepage"
              className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer ${mobileLogoHiddenAt}`}
            >
              <img
                src={logo.mobileSrc ?? logo.src}
                alt={logo.alt}
                className="h-[26px] w-[26px] object-contain"
                loading="eager"
              />
            </Link>
          )}
          {logo && showsTabletWordmark && (
            <Link
              to={url("/")}
              aria-label="Go to homepage"
              className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 cursor-pointer md:block lg:hidden"
              style={{ width: tabletLogoWidth }}
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="h-auto w-full"
                loading="eager"
              />
            </Link>
          )}
          {/* Left: Hamburger (mobile) | Hamburger + Logo (desktop) */}
          <div className="flex h-full items-center gap-3 lg:gap-1">
            <button
              type="button"
              className="flex h-[45px] items-center gap-1.5 text-accent"
              aria-label="Open navigation menu"
              onClick={() => setMobileNavOpen(true)}
            >
              <MobileMenuIcon />
              <span className="hidden font-grotesque text-sm font-bold md:inline lg:text-base">
                Menu
              </span>
            </button>

            {/* Search icon — FSA/WDH mobile only; opens the full-screen
                search overlay. */}
            {showsMobileSearchToggle && (
              <button
                ref={searchTriggerRef}
                type="button"
                className="max-lg:ml-2 text-search-icon lg:hidden"
                aria-label="Open search"
                data-testid="mobile-search-trigger"
                onClick={openMobileSearch}
              >
                <SvgSearchIcon width={20} height={20} strokeWidth={0} />
              </button>
            )}

            {/* Desktop-only logo — per-site dimensions (desktop only).
                Mobile logo lives in the center cell (centered, 26×26). */}
            {logo && (
              <Link
                to={url("/")}
                aria-label="Go to homepage"
                className="hidden cursor-pointer lg:ml-[30px] lg:block"
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  width={desktopLogoSize.w}
                  height={desktopLogoSize.h}
                  className="object-contain"
                  loading="eager"
                />
              </Link>
            )}
          </div>

          {/* Center column — desktop search only. The mobile/tablet logo is
              rendered above as an absolutely-positioned sibling so it sits at
              the row's true horizontal center regardless of side-cell widths. */}
          <div className="lg:flex lg:items-center">
            <div className="hidden w-full lg:block">
              <HeaderSearchBar />
            </div>
          </div>

          {/* Right: Account + Cart. `relative` anchors the SFCC-12333 cart
              reminder under the cart icon. */}
          <div className="relative flex h-full items-center justify-center lg:justify-end">
            {/* COM-482 PPR holes: sign-in state and the basket are per
                shopper. The cached shell shows the guest rendering until the
                hole resolves; in the browser the fallback never renders. */}
            <PprHole
              name="header-account"
              fallback={
                <AccountPopover
                  isLoggedIn={false}
                  accountDropdownContent={accountDropdownContent}
                  apiKey={config.api_key}
                />
              }
            >
              <AccountPopover
                isLoggedIn={isLoggedIn}
                accountDropdownContent={accountDropdownContent}
                apiKey={config.api_key}
              />
            </PprHole>
            <PprHole name="header-minicart" fallback={<Minicart />}>
              <Minicart />
            </PprHole>
            {/* SFCC-12333 cart reminder: shown from the shopper's basket and
                the `hec_cart_reminder_seen` session cookie, so it is per
                shopper and sits in its own hole. Both inputs are read in
                effects, so the guest fallback is the same empty live region
                the component renders before it decides. */}
            {showsCartReminder && (
              <PprHole
                name="header-cart-reminder"
                fallback={
                  <CartReminder className="absolute right-0 top-full z-10 mt-1" />
                }
              >
                <CartReminder className="absolute right-0 top-full z-10 mt-1" />
              </PprHole>
            )}
          </div>
        </div>

        {/* Mobile: HSA shows a full-width search affordance below the nav row.
            It's a button styled like the search input — tapping it opens the
            full-screen search overlay (FSA/WDH use the nav-row search icon
            instead). The real autocomplete input lives inside the overlay. */}
        {mobileSearchRowAlwaysVisible && (
          <div className="px-2.5 pb-[5px] lg:hidden">
            <button
              ref={searchTriggerRef}
              type="button"
              onClick={openMobileSearch}
              aria-label="Open search"
              data-testid="mobile-search-trigger"
              className="flex h-[54px] w-full items-center justify-between rounded-lg border border-gray-200 bg-white pl-4 pr-3 text-left font-grotesque text-sm text-gray-05"
            >
              <span className="truncate">Search Eligible Products</span>
              <SvgSearchIcon
                width={18}
                height={18}
                className="shrink-0 text-blue-03 [stroke-width:0.5]"
                aria-hidden="true"
              />
            </button>
          </div>
        )}

        {/* Soft-navigation progress. Lives here, on the sticky header, so it
            covers EVERY storefront route and both viewports — the previous
            indicator was inside `Meganav`, which only the checkout layout
            mounts, so PDP/PLP navigation had no feedback at all. `position:
            sticky` establishes the containing block, so this anchors to the
            header's bottom edge. */}
        <RouteChangeProgress />
      </header>

      {/* -- 3. Share-of-Wallet Links Bar (affiliate-nav-bar) — FSA/HSA only;
             WDH has no SOW bar in production. No hardcoded fallback by design
             (prefer empty over wrong, as with the footer). The `<nav>` shell
             sits inside the content check so a site with no entry emits no
             empty landmark. -- */}
      {hasSowBar && affiliateNavBar && (
        <nav
          className="no-scrollbar overflow-x-auto bg-white"
          aria-label="Health categories"
        >
          <VisibleChromeContent
            model={ModelMap.affiliateNavBar}
            content={affiliateNavBar as BuilderContent}
            apiKey={config.api_key}
          />
        </nav>
      )}

      {/* -- 4. TPA Balance Tracker (FSA + HSA; WDH has no TPA flows) -- */}
      {hasTpaBalance && (
        // Tablet + desktop: render below the header, matching the #maincontent
        // section width (and shrinking with the sections). On mobile (<sm) this
        // is hidden — the balance display renders inside the account popover
        // instead, under Perks (the TPABalance Builder block with mobileOnly).
        <div className="balance-tracker-active min-[992px]:mx-auto min-[992px]:w-full min-[992px]:px-[87px] min-[1200px]:max-w-[1458px] mx-[3px] hidden sm:block">
          <TpaBalance balancePopupText={DEFAULT_BALANCE_POPUP_TEXT} />
        </div>
      )}

      {/* -- Mobile nav drawer -- */}
      {(overlaysReady || mobileNavOpen) && (
        // OverlayBoundary (PR #1042 blocker 3): a rejected nav-drawer chunk
        // must not unmount the whole page — hamburger degrades to a no-op.
        <OverlayBoundary overlay="mobile-nav" fallback={null}>
          <Suspense fallback={null}>
            <HecMobileNav
              open={mobileNavOpen}
              onClose={() => setMobileNavOpen(false)}
              categories={categories}
              hostname={hostname}
              navDrawerContent={navDrawerContent}
            />
          </Suspense>
        </OverlayBoundary>
      )}

      {/* -- Mobile full-screen search overlay -- */}
      {/* Hidden, focusable input that primes the iOS soft keyboard. iOS only
          raises the keyboard when .focus() runs inside a user gesture, but the
          overlay's real input mounts (via portal) AFTER the tap. openMobileSearch
          focuses this synchronously to open the keyboard, then the overlay
          transfers focus to the real input, keeping it up. Must stay focusable
          (no display:none/visibility:hidden) but out of the tab/AT order. */}
      {/* `data-search-pending`: marks this as an adoptable keystroke buffer —
          characters typed here while the lazy search chunk streams are carried
          into the real input by adoptPendingSearch() (PR #1042 blocker 4)
          instead of being silently swallowed. */}
      <input
        ref={searchWarmupRef}
        type="text"
        tabIndex={-1}
        aria-hidden="true"
        data-search-pending=""
        className="pointer-events-none fixed left-0 top-0 h-px w-px opacity-0"
      />
      {(overlaysReady || mobileSearchOpen) && (
        // OverlayBoundary (PR #1042 blocker 3).
        <OverlayBoundary overlay="mobile-search-overlay" fallback={null}>
          <Suspense fallback={null}>
            <MobileSearchOverlay
              open={mobileSearchOpen}
              onOpenChange={setMobileSearchOpen}
              triggerRef={searchTriggerRef}
            />
          </Suspense>
        </OverlayBoundary>
      )}
    </>
  );
}
